import { useState, type ReactNode } from 'react';
import { Dropdown, Spinner } from 'react-bootstrap';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useToast } from 'context/ToastContext';
import * as apiObject from 'api/apiObjects';
import type { ObjectType } from '@econtractor/types';
import SolarIcon from 'components/icons/SolarIcon';

export type ObjectPinAction = 'top' | 'bottom' | 'reset';

export type FetchObjectsFn = (
    force?: boolean,
    options?: { silent?: boolean },
) => Promise<void | null>;

export interface ObjectPinProps {
    vobject: ObjectType;
    fetchObjects: FetchObjectsFn;
}

const ObjectPin = ({ vobject, fetchObjects }: ObjectPinProps) => {
    const { t } = useTranslation(['Objects', 'common']);
    const { showToast } = useToast();
    const [loading, setLoading] = useState(false);
    const pin = Number(vobject?.pin || 0);

    const handlePin = async (action: ObjectPinAction) => {
        if (loading) return;
        setLoading(true);
        const stat = await apiObject.setPin(vobject.ID, action);
        if (stat) {
            await fetchObjects(true, { silent: true });
        } else {
            showToast(t('common:messError'), 'danger');
        }
        setLoading(false);
    };

    const renderMenuItem = (icon: ReactNode, title: string, subtitle: string, action: ObjectPinAction) => (
        <Dropdown.Item
            as="button"
            disabled={loading}
            onClick={(e) => {
                void handlePin(action);
                e.currentTarget.blur();
            }}
        >
            <div className="d-flex align-items-start w-100 text-start">
                <span className="d-inline-flex align-items-center justify-content-center flex-shrink-0 me-2 item-icon">
                    {icon}
                </span>
                <div className="flex-grow-1 min-w-0">
                    <div className="fw-medium">{title}</div>
                    <small className="text-muted warehouse-team-menu-hint">{subtitle}</small>
                </div>
            </div>
        </Dropdown.Item>
    );

    const menu = (
        <Dropdown.Menu
            className="p-2 shadow-sm warehouse-team-menu"
            popperConfig={{
                strategy: 'fixed',
                modifiers: [
                    { name: 'preventOverflow', enabled: false },
                    {
                        name: 'flip',
                        options: { fallbackPlacements: ['top-end', 'bottom-end'] },
                    },
                ],
            }}
            renderOnMount
        >
            {pin <= 0 && renderMenuItem(
                <SolarIcon name="pin-angle-fill" />,
                t('objectPopup.pinTop'),
                t('objectPopup.pinTopHint'),
                'top',
            )}
            {pin >= 0 && renderMenuItem(
                <SolarIcon name="pin-angle-fill" style={{ transform: 'rotate(180deg)', display: 'inline-block' }} />,
                t('objectPopup.pinBottom'),
                t('objectPopup.pinBottomHint'),
                'bottom',
            )}
            {pin !== 0 && renderMenuItem(
                <SolarIcon name="pin-angle" />,
                t('objectPopup.pinReset'),
                t('objectPopup.pinResetHint'),
                'reset',
            )}
        </Dropdown.Menu>
    );

    const pinIcon = pin === 0
        ? <SolarIcon name="pin-angle" />
        : (
            <SolarIcon name="pin-angle-fill" style={pin < 0 ? { transform: 'rotate(180deg)', display: 'inline-block' } : undefined} />
        );

    return (
        <Dropdown align="end">
            <Dropdown.Toggle
                className="p-1 py-0"
                size="sm"
                variant="light-secondary"
                id={`dropdown-object-pin-${vobject.ID}`}
                disabled={loading}
                title={t('objectPopup.pin')}
            >
                {loading
                    ? <Spinner animation="border" size="sm" />
                    : pinIcon}
            </Dropdown.Toggle>

            {typeof document !== 'undefined' ? createPortal(menu, document.body) : menu}
        </Dropdown>
    );
};

export default ObjectPin;
